export const API_BASE = import.meta.env.VITE_API_BASE || 'https://dietamenu.schedy.app';

export class ApiError extends Error {
  status: number;
  /** Codice `error` dal body backend (es. 'patient_cap_reached'). '' se body non-JSON. */
  code: string;
  details: string | null;
  constructor(status: number, error: string, details: string | null) {
    super(`API ${status}: ${error}${details ? ` (${details})` : ''}`);
    this.status = status;
    this.code = error;
    this.details = details;
  }
}

/** True se l'errore è un 402 del paywall (requirePaid → upgrade richiesto). */
export function isPaymentRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 402;
}

/**
 * Sprint 10 Fase 3 — distingue i 402 di ENFORCEMENT (cap/quota/eco) dal 402
 * di paywall generico. Ritorna il reason per il LimitReachedModal, o null.
 */
export type LimitReason = 'patient_cap' | 'ai_quota' | 'ai_smart';
export function limitReason(err: unknown): LimitReason | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  switch (err.code) {
    case 'patient_cap_reached': return 'patient_cap';
    case 'ai_quota_exceeded': return 'ai_quota';
    case 'ai_smart_requires_upgrade': return 'ai_smart';
    default: return null;
  }
}

// Sprint 9.D — Session-Id header fallback per Chrome CHIPS partitioning.
// localStorage key dove salviamo l'id sessione dopo login/register.
export const SESSION_ID_KEY = 'dietamenu.session_id';

export function getStoredSessionId(): string | null {
  try { return window.localStorage.getItem(SESSION_ID_KEY); } catch { return null; }
}
export function setStoredSessionId(sid: string | null): void {
  try {
    if (sid) window.localStorage.setItem(SESSION_ID_KEY, sid);
    else window.localStorage.removeItem(SESSION_ID_KEY);
  } catch { /* incognito strict */ }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const sid = getStoredSessionId();
  const extraHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  if (sid) extraHeaders['X-Session-Id'] = sid;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { ...extraHeaders, ...init?.headers },
  });
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    let details: string | null = null;
    try {
      const data = await res.json() as { error?: string; details?: string };
      if (data && typeof data.error === 'string') errMsg = data.error;
      if (data && typeof data.details === 'string') details = data.details;
    } catch {
      // body non-JSON, manteniamo HTTP code
    }
    throw new ApiError(res.status, errMsg, details);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Sprint 6.A — Coach types (mirror src/pro/coach.ts response shapes)
// ---------------------------------------------------------------------------

export interface CoachMeResponse {
  pro_kind: 'nutri' | 'coach' | 'both' | null;
  athletes_count: number;
  active_programs: number;
  live_sessions_count: number;
}

export type AthleteStatus = 'active' | 'live' | 'attention' | 'deload';

export interface AthleteRow {
  id: string;
  name: string;
  age: number | null;
  sex: 'M' | 'F' | null;
  goal: string | null;
  program_name: string | null;
  adherence_7d: number | null;
  volume_week_kg: number | null;
  pr_30d: number;
  last_session_at: string | null;
  status: AthleteStatus;
}

export interface AthletesResponse {
  athletes: AthleteRow[];
}

export interface CoachLiveSession {
  session_id: string;
  athlete_id: string;
  athlete_name: string;
  program_name: string | null;
  focus: string | null;
  started_at: string | null;
  exercises: Array<{
    id: string;
    name: string;
    variant: string | null;
    position: number;
    sets: Array<{
      id: string;
      set_n: number;
      weight_kg: number | null;
      reps: number | null;
      rpe: number | null;
      status: 'todo' | 'live' | 'done' | 'skipped';
    }>;
  }>;
}

export interface LiveSessionResponse {
  session: CoachLiveSession | null;
}

export interface CoachProgramRow {
  id: string;
  athlete_id: string;
  athlete_name: string;
  name: string;
  goal: string | null;
  weeks_total: number;
  start_date: string | null;
  end_date: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  sessions_total: number;
  sessions_done: number;
}

export interface ProgramsResponse {
  programs: CoachProgramRow[];
}

export interface RecoveryDayCell {
  date: string;
  score: number | null;
}

export interface RecoveryAthlete {
  athlete_id: string;
  name: string;
  days: RecoveryDayCell[];
}

export interface RecoveryHeatmapResponse {
  athletes: RecoveryAthlete[];
  days: number;
}

export interface TopPRRow {
  athlete_id: string;
  name: string;
  exercise_name: string;
  kind: '1rm' | '3rm' | '5rm' | 'amrap' | 'volume';
  value_kg: number | null;
  value_reps: number | null;
  achieved_at: string;
}

export interface TopPRResponse {
  records: TopPRRow[];
  days: number;
}

export interface VolumeDayPoint {
  day_label: string;
  iso_date: string;
  kg_total: number;
}

export interface VolumeWeekResponse {
  points: VolumeDayPoint[];
}

export type CoachAlertKind = 'missed' | 'overtrain' | 'deload-due';

export interface CoachAlert {
  kind: CoachAlertKind;
  athlete_id: string;
  name: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
}

export interface CoachAlertsResponse {
  alerts: CoachAlert[];
}

export type FeedKind = 'pr' | 'session_done' | 'note';

export interface FeedItem {
  kind: FeedKind;
  athlete_id: string;
  name: string;
  message: string;
  created_at: string;
}

export interface CoachFeedResponse {
  feed: FeedItem[];
}

// ---------------------------------------------------------------------------
// Sprint 7.2 — Editor wizard types & mutations
// ---------------------------------------------------------------------------

export type WorkoutProgramStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export interface ExerciseFull {
  id: string;
  position: number;
  name: string;
  variant: string | null;
  sets_planned: number;
  reps_target: string | null;
  rest_seconds: number | null;
  notes: string | null;
}

export interface SessionFull {
  id: string;
  week_n: number;
  day_n: number;
  name: string;
  focus: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  duration_estimated_min: number | null;
  coach_note: string | null;
  status: string;
  exercises: ExerciseFull[];
}

export interface ProgramFull {
  id: string;
  athlete_id: string;
  athlete_name: string;
  coach_id: string | null;
  name: string;
  goal: string | null;
  weeks_total: number;
  start_date: string | null;
  end_date: string | null;
  status: WorkoutProgramStatus;
  created_at: string;
  updated_at: string;
  sessions: SessionFull[];
}

export interface ProgramFullResponse {
  program: ProgramFull;
}

export interface CreateProgramBody {
  athlete_id: string;
  name: string;
  goal: string | null;
  weeks_total: number;
  start_date: string | null;
  end_date: string | null;
  status?: WorkoutProgramStatus;
}

export interface UpdateProgramBody {
  name?: string;
  goal?: string | null;
  weeks_total?: number;
  start_date?: string | null;
  end_date?: string | null;
  status?: WorkoutProgramStatus;
}

export interface BulkSessionInput {
  id: string | null;
  week_n: number;
  day_n: number;
  name: string;
  focus: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  duration_estimated_min: number | null;
  coach_note: string | null;
  exercises: BulkExerciseInput[];
}

export interface BulkExerciseInput {
  id: string | null;
  position: number;
  name: string;
  variant: string | null;
  sets_planned: number;
  reps_target: string | null;
  rest_seconds: number | null;
  notes: string | null;
}

export interface BulkSaveBody {
  name: string;
  goal: string | null;
  weeks_total: number;
  start_date: string | null;
  end_date: string | null;
  status: WorkoutProgramStatus;
  sessions: BulkSessionInput[];
}

export function createCoachProgram(body: CreateProgramBody): Promise<ProgramFullResponse> {
  return apiFetch<ProgramFullResponse>('/api/pro/coach/program', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getCoachProgramFull(programId: string): Promise<ProgramFullResponse> {
  return apiFetch<ProgramFullResponse>(`/api/pro/coach/program/${encodeURIComponent(programId)}/full`);
}

export function updateCoachProgram(programId: string, body: UpdateProgramBody): Promise<ProgramFullResponse> {
  return apiFetch<ProgramFullResponse>(`/api/pro/coach/program/${encodeURIComponent(programId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteCoachProgram(programId: string): Promise<{ ok: true; archived: boolean }> {
  return apiFetch<{ ok: true; archived: boolean }>(`/api/pro/coach/program/${encodeURIComponent(programId)}`, {
    method: 'DELETE',
  });
}

export function bulkSaveCoachProgram(programId: string, body: BulkSaveBody): Promise<ProgramFullResponse> {
  return apiFetch<ProgramFullResponse>(`/api/pro/coach/program/${encodeURIComponent(programId)}/bulk-save`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface CreateSessionBody {
  week_n: number;
  day_n: number;
  name: string;
  focus?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  duration_estimated_min?: number | null;
  coach_note?: string | null;
}

export interface UpdateSessionBody {
  week_n?: number;
  day_n?: number;
  name?: string;
  focus?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  duration_estimated_min?: number | null;
  coach_note?: string | null;
}

export function createCoachSession(programId: string, body: CreateSessionBody): Promise<{ session: SessionFull }> {
  return apiFetch<{ session: SessionFull }>(`/api/pro/coach/program/${encodeURIComponent(programId)}/session`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateCoachSession(sessionId: string, body: UpdateSessionBody): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/coach/session/${encodeURIComponent(sessionId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteCoachSession(sessionId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/coach/session/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
}

export interface CreateExerciseBody {
  position: number;
  name: string;
  variant?: string | null;
  sets_planned: number;
  reps_target?: string | null;
  rest_seconds?: number | null;
  notes?: string | null;
}

export type UpdateExerciseBody = Partial<CreateExerciseBody>;

export function createCoachExercise(sessionId: string, body: CreateExerciseBody): Promise<{ exercise: ExerciseFull }> {
  return apiFetch<{ exercise: ExerciseFull }>(`/api/pro/coach/session/${encodeURIComponent(sessionId)}/exercise`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateCoachExercise(exerciseId: string, body: UpdateExerciseBody): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/coach/exercise/${encodeURIComponent(exerciseId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteCoachExercise(exerciseId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/coach/exercise/${encodeURIComponent(exerciseId)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Sprint 8.2 — Appointments types & helpers
// Mirror di src/pro/appointments.ts + src/db/schema.ts Appointment.
// ---------------------------------------------------------------------------

export type AppointmentType =
  | 'visita'
  | 'follow-up'
  | 'sessione'
  | 'misurazione'
  | 'consulenza'
  | 'altro';

export type AppointmentStatus =
  | 'scheduled'
  | 'completed'
  | 'canceled'
  | 'no-show';

export interface Appointment {
  id: string;
  patient_id: string;
  pro_id: string;
  pro_role: 'nutri' | 'coach';
  type: AppointmentType;
  title: string | null;
  start_at: string;
  duration_min: number;
  location: string | null;
  notes: string | null;
  status: AppointmentStatus;
  reminded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppointmentWithPatient extends Appointment {
  patient_name: string;
  patient_human_id: string | null;
}

export interface AppointmentsListResponse {
  appointments: AppointmentWithPatient[];
  from: string;
  to: string;
  pro_role: 'nutri' | 'coach' | 'all';
}

export interface AppointmentResponse {
  appointment: AppointmentWithPatient;
}

export interface CreateAppointmentBody {
  patient_id: string;
  pro_role: 'nutri' | 'coach';
  type: AppointmentType;
  start_at: string;            // ISO 8601 (UTC). datetime-local è ammesso (verra' normalizzato server-side).
  duration_min?: number;
  title?: string | null;
  location?: string | null;
  notes?: string | null;
}

export interface UpdateAppointmentBody {
  type?: AppointmentType;
  start_at?: string;
  duration_min?: number;
  title?: string | null;
  location?: string | null;
  notes?: string | null;
  status?: AppointmentStatus;
  pro_role?: 'nutri' | 'coach';
}

export interface ListAppointmentsParams {
  from?: string;       // YYYY-MM-DD
  to?: string;         // YYYY-MM-DD
  pro_role?: 'nutri' | 'coach' | 'all';
}

function qs(params: { [k: string]: string | number | undefined | null }): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export function listAppointments(
  params: ListAppointmentsParams = {}
): Promise<AppointmentsListResponse> {
  return apiFetch<AppointmentsListResponse>(
    `/api/pro/appointments${qs({ ...params })}`
  );
}

export function listAppointmentsToday(): Promise<{ appointments: AppointmentWithPatient[] }> {
  return apiFetch<{ appointments: AppointmentWithPatient[] }>('/api/pro/appointments/today');
}

export function listAppointmentsUpcoming(limit = 5): Promise<{ appointments: AppointmentWithPatient[] }> {
  return apiFetch<{ appointments: AppointmentWithPatient[] }>(
    `/api/pro/appointments/upcoming?limit=${limit}`
  );
}

export function getAppointment(id: string): Promise<AppointmentResponse> {
  return apiFetch<AppointmentResponse>(`/api/pro/appointments/${encodeURIComponent(id)}`);
}

export function createAppointment(body: CreateAppointmentBody): Promise<AppointmentResponse> {
  return apiFetch<AppointmentResponse>('/api/pro/appointments', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateAppointment(id: string, body: UpdateAppointmentBody): Promise<AppointmentResponse> {
  return apiFetch<AppointmentResponse>(`/api/pro/appointments/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteAppointment(id: string): Promise<{ ok: true; canceled: boolean }> {
  return apiFetch<{ ok: true; canceled: boolean }>(
    `/api/pro/appointments/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
}

export function setAppointmentStatus(
  id: string,
  status: AppointmentStatus,
): Promise<AppointmentResponse> {
  return apiFetch<AppointmentResponse>(
    `/api/pro/appointments/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: JSON.stringify({ status }) }
  );
}

// ---------------------------------------------------------------------------
// Sprint 9.3 — Pro studio profile (profilo + branding + role)
// ---------------------------------------------------------------------------

export type StudioProKind = 'nutri' | 'coach' | 'both';

export interface StudioProfile {
  id: string;
  name: string;
  email: string | null;
  pro_code: string | null;
  /** Fase 3 — codici invito per-ruolo, valorizzati (lazy) SOLO per pro_kind='both'. */
  pro_code_nutri: string | null;
  pro_code_coach: string | null;
  pro_kind: StudioProKind | null;
  studio_name: string | null;
  studio_logo_r2_key: string | null;
  studio_logo_url: string | null;
  studio_color_primary: string | null;
  studio_color_accent: string | null;
  pro_note_text: string | null;
  pro_note_updated_at: string | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  subscription_current_period_end: string | null;
}

export interface UpdateProfileBody {
  name?: string;
  studio_name?: string | null;
  studio_color_primary?: string | null;
  studio_color_accent?: string | null;
  pro_kind?: StudioProKind | null;
  pro_note_text?: string | null;
}

export interface UploadLogoResult {
  studio_logo_r2_key: string;
  studio_logo_url: string;
}

export function getStudioProfile(): Promise<StudioProfile> {
  return apiFetch<StudioProfile>('/api/pro/profile');
}

export function updateStudioProfile(body: UpdateProfileBody): Promise<StudioProfile> {
  return apiFetch<StudioProfile>('/api/pro/profile', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Upload del logo studio. Bypass apiFetch perché multipart/form-data non vuole
 * `Content-Type: application/json`. Ritorna r2_key + url assoluto al backend.
 */
export async function uploadStudioLogo(file: File): Promise<UploadLogoResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_BASE}/api/pro/profile/logo`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    let details: string | null = null;
    try {
      const data = await res.json() as { error?: string; details?: string };
      if (data && typeof data.error === 'string') errMsg = data.error;
      if (data && typeof data.details === 'string') details = data.details;
    } catch {
      // ignore body parse error
    }
    throw new ApiError(res.status, errMsg, details);
  }
  return res.json();
}

export function deleteStudioLogo(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/pro/profile/logo', { method: 'DELETE' });
}

/**
 * URL assoluto del logo del pro corrente. Usato per `<img src>`.
 * Aggiunge `?v={updated_at}` per cache-busting dopo upload/remove.
 */
export function studioLogoSrc(profile: Pick<StudioProfile, 'studio_logo_url'>, cacheKey?: string): string | null {
  if (!profile.studio_logo_url) return null;
  const base = `${API_BASE}${profile.studio_logo_url}`;
  return cacheKey ? `${base}?v=${encodeURIComponent(cacheKey)}` : base;
}

// ---------------------------------------------------------------------------
// Sprint 9.2 — Pro Messaggi v2 (chat bidirezionale pro<->paziente)
// Mirror dei types backend (src/db/schema.ts) + helper REST.
// ---------------------------------------------------------------------------

export type ProMessageFromRole = 'pro' | 'patient';

export interface ProMessage {
  id: string;
  pro_id: string;
  patient_id: string;
  from_role: ProMessageFromRole;
  text: string;
  read_at: string | null;
  created_at: string;
}

export interface ProMessageThreadForPro {
  patient_id: string;
  patient_name: string;
  patient_human_id: string | null;
  last_msg: string;
  last_at: string;
  unread_count: number;
}

export interface ProMessageThreadsResponse {
  threads: ProMessageThreadForPro[];
}

export interface ProMessageThreadResponse {
  messages: ProMessage[];
}

export interface ProMessageSendBody {
  text: string;
}

export interface ProMessageSendResponse {
  message: ProMessage;
}

export interface MarkReadResponse {
  updated: number;
}

export function listProMessageThreads(): Promise<ProMessageThreadsResponse> {
  return apiFetch<ProMessageThreadsResponse>('/api/pro/messages/threads');
}

export interface GetThreadParams {
  patientId: string;
  limit?: number;
  before?: string | null;
}

export function getProMessageThread(
  params: GetThreadParams
): Promise<ProMessageThreadResponse> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.before) qs.set('before', params.before);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<ProMessageThreadResponse>(
    `/api/pro/messages/thread/${encodeURIComponent(params.patientId)}${suffix}`
  );
}

export function sendProMessage(
  patientId: string,
  text: string
): Promise<ProMessageSendResponse> {
  return apiFetch<ProMessageSendResponse>(
    `/api/pro/messages/thread/${encodeURIComponent(patientId)}`,
    {
      method: 'POST',
      body: JSON.stringify({ text } satisfies ProMessageSendBody),
    }
  );
}

export function markProMessageThreadRead(
  patientId: string
): Promise<MarkReadResponse> {
  return apiFetch<MarkReadResponse>(
    `/api/pro/messages/thread/${encodeURIComponent(patientId)}/read`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Sprint 8.4 — Cal.com OAuth integration (status / sync / event-types / push)
// ---------------------------------------------------------------------------

export interface CalcomStatus {
  connected: boolean;
  configured: boolean;
  cal_username?: string | null;
  cal_email?: string | null;
  last_synced_at?: string | null;
  last_error?: string | null;
  last_error_at?: string | null;
  connected_at?: string | null;
  expires_at?: string | null;
}

export interface CalcomEventType {
  id: string;
  title: string;
  slug: string | null;
  length_min: number | null;
  hidden: boolean;
}

export interface CalcomConflictInfo {
  cal_booking_id: string;
  cal_start_at: string;
  cal_duration_min: number;
  manual_appointment_id: string;
  manual_start_at: string;
  manual_duration_min: number;
  patient_name: string | null;
}

export interface CalcomSyncResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: number;
  conflicts: CalcomConflictInfo[];
  from_iso: string;
  to_iso: string;
}

export interface CalcomPushResult {
  appointment_id: string;
  cal_booking_id: string;
  cal_booking_uid: string;
}

export function getCalcomStatus(): Promise<CalcomStatus> {
  return apiFetch<CalcomStatus>('/api/pro/calcom/status');
}

export function getCalcomEventTypes(): Promise<{ event_types: CalcomEventType[] }> {
  return apiFetch<{ event_types: CalcomEventType[] }>('/api/pro/calcom/event-types');
}

export function disconnectCalcom(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/pro/calcom/disconnect', { method: 'POST' });
}

export function syncCalcom(range?: { from?: string; to?: string }): Promise<CalcomSyncResult> {
  return apiFetch<CalcomSyncResult>('/api/pro/calcom/sync', {
    method: 'POST',
    body: JSON.stringify(range || {}),
  });
}

export function pushAppointmentToCalcom(
  appointment_id: string,
  event_type_id: string | number,
): Promise<CalcomPushResult> {
  return apiFetch<CalcomPushResult>('/api/pro/calcom/push', {
    method: 'POST',
    body: JSON.stringify({ appointment_id, event_type_id }),
  });
}

export function cancelAppointmentOnCalcom(
  appointment_id: string,
  reason?: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/pro/calcom/cancel', {
    method: 'POST',
    body: JSON.stringify({ appointment_id, reason }),
  });
}

/**
 * URL absolute al worker per il redirect 302 OAuth (connect).
 * Usato come window.location = ... (no apiFetch — è un GET redirect server).
 */
export function calcomConnectUrl(): string {
  return `${API_BASE}/api/pro/calcom/connect`;
}

// ---------------------------------------------------------------------------
// Sprint 9.B — Diet plan editor wizard (porting da SPA vanilla in React)
// Mirror src/db/schema.ts (DietPlan, Meal, MealItem) + endpoint /api/pro/plans*.
// ---------------------------------------------------------------------------

export type MealType =
  | 'breakfast'
  | 'snack_morning'
  | 'lunch'
  | 'snack_afternoon'
  | 'dinner';

export const MEAL_TYPES: ReadonlyArray<{ value: MealType; label: string }> = [
  { value: 'breakfast', label: 'Colazione' },
  { value: 'snack_morning', label: 'Spuntino mattina' },
  { value: 'lunch', label: 'Pranzo' },
  { value: 'snack_afternoon', label: 'Merenda' },
  { value: 'dinner', label: 'Cena' },
];

export const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'] as const;

/** Riga semplificata dalla lista piani GET /api/pro/plans. */
export interface DietPlanRow {
  id: string;
  title: string;
  patient_id: string;
  start_date: string;
  end_date: string;
  created_at: string;
  patient_name: string | null;
  meal_count: number;
  /** Migration 0035 (multi-piano) — 'active' (in uso dal paziente) | 'archived'. NULL = legacy pre-0035. */
  status: 'active' | 'archived' | null;
  /** Migration 0035 — user id del creatore. NULL = legacy pre-0035. */
  created_by: string | null;
}

export interface PlansListResponse {
  plans: DietPlanRow[];
}

/** Item dell'editor (mirror createPlanFromScratch payload + sort_order locale). */
export interface PlanEditorItem {
  ingredient: string;
  quantity: string | null;
  unit: string | null;
  calories?: number | null; // populated only client-side from CREA lookup hint
}

export interface PlanEditorMeal {
  /** Identificativo locale uuid-like (client-only, mai inviato al server). */
  client_id: string;
  day_of_week: number;          // 1..7 (1=lun)
  meal_type: MealType;
  name: string;
  items: PlanEditorItem[];
  week_of_plan?: number | null;
  variant_group?: number | null;
  variant_label?: string | null;
  is_fasting?: 0 | 1;
}

export interface CreatePlanFromScratchBody {
  patient_id: string;
  title: string;
  start_date: string;
  end_date: string;
  meals: Array<{
    day_of_week: number;
    meal_type: MealType;
    name: string;
    items: Array<{ ingredient: string; quantity: string | null; unit: string | null }>;
    week_of_plan?: number | null;
    variant_group?: number | null;
    variant_label?: string | null;
    is_fasting?: 0 | 1;
  }>;
}

export interface CreatePlanFromScratchResponse {
  plan_id: string;
  meals_created: number;
  items_created: number;
  enriched_count: number;
}

export interface ClonePlanBody {
  patient_id: string;
  title?: string;
  start_date?: string;
}

export interface ClonePlanResponse {
  plan_id: string;
  meals: number;
  items: number;
}

export interface NutritionSearchResult {
  food_code: string;
  name: string;
  kcal_per_100g: number;
}

export interface NutritionSearchResponse {
  results: NutritionSearchResult[];
}

export interface MealSuggestSmartRequest {
  patient_id: string;
  meal_type: MealType;
  context?: string | null;
}

export interface MealSuggestItem {
  ingredient: string;
  quantity: string | null;
  unit: string | null;
}

export interface MealSuggestSmartResponse {
  name: string;
  items: MealSuggestItem[];
  reasoning?: string | null;
}

export interface MealSuggestSimpleRequest {
  meal_type: MealType;
  context?: string | null;
}

export interface MealSuggestSimpleResponse {
  name: string;
  items: MealSuggestItem[];
}

export function listDietPlans(): Promise<PlansListResponse> {
  return apiFetch<PlansListResponse>('/api/pro/plans');
}

export function createPlanFromScratch(
  body: CreatePlanFromScratchBody
): Promise<CreatePlanFromScratchResponse> {
  return apiFetch<CreatePlanFromScratchResponse>('/api/pro/plans/create-from-scratch', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteDietPlan(planId: string): Promise<{ deleted: true }> {
  return apiFetch<{ deleted: true }>(`/api/pro/plans/${encodeURIComponent(planId)}`, {
    method: 'DELETE',
  });
}

export function cloneDietPlan(
  sourceId: string,
  body: ClonePlanBody
): Promise<ClonePlanResponse> {
  return apiFetch<ClonePlanResponse>(`/api/pro/plans/${encodeURIComponent(sourceId)}/clone`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function searchNutrition(q: string, limit = 10): Promise<NutritionSearchResponse> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<NutritionSearchResponse>(`/api/nutrition/search?${qs.toString()}`);
}

// ─── Catalogo cibi CREA (browser completo) ────────────────────────────────────

export interface NutritionCatalogItem {
  food_code: string;
  name: string;
  category: string | null;
  portion_g: number | null;
  kcal_per_100g: number;
  proteins_per_100g: number | null;
  carbs_per_100g: number | null;
  fats_per_100g: number | null;
  fiber_per_100g: number | null;
}

export interface NutritionCatalogResponse {
  results: NutritionCatalogItem[];
}

/** Intero catalogo CREA (~900 alimenti). Dato statico → la UI lo cachea e filtra in-memory. */
export function getNutritionCatalog(): Promise<NutritionCatalogResponse> {
  return apiFetch<NutritionCatalogResponse>('/api/nutrition/catalog');
}

export function suggestMealSmart(
  body: MealSuggestSmartRequest
): Promise<MealSuggestSmartResponse> {
  return apiFetch<MealSuggestSmartResponse>('/api/pro/meal/suggest-smart', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function suggestMealSimple(
  body: MealSuggestSimpleRequest
): Promise<MealSuggestSimpleResponse> {
  return apiFetch<MealSuggestSimpleResponse>('/api/pro/meal/suggest', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.D bug-fix — Wizard creazione paziente from pro-web (POST /api/patients)
// ---------------------------------------------------------------------------

export interface CreatePatientBody {
  name: string;
  email: string;
  phone?: string | null;
}

export interface CreatePatientResponse {
  patient: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    human_id: string | null;
  };
}

export function createPatient(body: CreatePatientBody): Promise<CreatePatientResponse> {
  return apiFetch<CreatePatientResponse>('/api/patients', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.B closure — Edit mode (GET singolo + PUT) + Plan templates
// ---------------------------------------------------------------------------

/** Meal singolo come ritornato da GET /api/pro/plans/:id (hydrate edit). */
export interface DietPlanDetailMeal {
  id: string;
  day_of_week: number;
  week_of_plan: number | null;
  meal_type: MealType;
  name: string;
  calories: number;
  proteins: number;
  carbs: number;
  fats: number;
  variant_group: number | null;
  variant_label: string | null;
  is_fasting: 0 | 1;
  items: Array<{ ingredient: string; quantity: string | null; unit: string | null; calories: number | null }>;
}

export interface DietPlanDetail {
  plan: {
    id: string;
    patient_id: string;
    dietologist_id: string;
    title: string;
    start_date: string;
    end_date: string;
    notes: string | null;
    created_at: string;
  };
  meals: DietPlanDetailMeal[];
}

export interface UpdateDietPlanBody {
  title: string;
  start_date: string;
  end_date: string;
  notes?: string | null;
  meals: CreatePlanFromScratchBody['meals'];
}

export interface UpdateDietPlanResponse {
  ok: true;
  plan_id: string;
  meals_created: number;
  items_created: number;
}

export function getDietPlanDetail(planId: string): Promise<DietPlanDetail> {
  return apiFetch<DietPlanDetail>(`/api/pro/plans/${encodeURIComponent(planId)}`);
}

export function updateDietPlan(planId: string, body: UpdateDietPlanBody): Promise<UpdateDietPlanResponse> {
  return apiFetch<UpdateDietPlanResponse>(`/api/pro/plans/${encodeURIComponent(planId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Riga lista templates GET /api/pro/plan-templates. */
export interface PlanTemplateRow {
  id: string;
  name: string;
  description: string | null;
  weeks: number;
  created_at: string;
  updated_at: string;
}

export interface PlanTemplatesListResponse {
  templates: PlanTemplateRow[];
}

export interface PlanTemplateDetailMeal {
  day_of_week: number;
  meal_type: MealType;
  name: string;
  items: Array<{ ingredient: string; quantity: string | null; unit: string | null }>;
  week_of_plan?: number | null;
  variant_group?: number | null;
  variant_label?: string | null;
  is_fasting?: 0 | 1;
}

export interface PlanTemplateDetailResponse {
  template: PlanTemplateRow & { archived_at: string | null };
  meals: PlanTemplateDetailMeal[];
}

export interface CreatePlanTemplateBody {
  name: string;
  description?: string | null;
  weeks: number;
  template_json: string;
}

export interface UpdatePlanTemplateBody {
  name?: string;
  description?: string | null;
  weeks?: number;
  template_json?: string;
}

export interface ApplyPlanTemplateBody {
  patient_id: string;
  title?: string;
  start_date: string;
}

export interface ApplyPlanTemplateResponse {
  plan_id: string;
  meals_created: number;
}

export function listPlanTemplates(): Promise<PlanTemplatesListResponse> {
  return apiFetch<PlanTemplatesListResponse>('/api/pro/plan-templates');
}

export function getPlanTemplate(id: string): Promise<PlanTemplateDetailResponse> {
  return apiFetch<PlanTemplateDetailResponse>(`/api/pro/plan-templates/${encodeURIComponent(id)}`);
}

export function createPlanTemplate(body: CreatePlanTemplateBody): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/api/pro/plan-templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updatePlanTemplate(id: string, body: UpdatePlanTemplateBody): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/plan-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deletePlanTemplate(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/plan-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function applyPlanTemplate(
  id: string,
  body: ApplyPlanTemplateBody
): Promise<ApplyPlanTemplateResponse> {
  return apiFetch<ApplyPlanTemplateResponse>(`/api/pro/plan-templates/${encodeURIComponent(id)}/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.5 — Analytics PRO types & helpers
// Mirror di src/pro/analytics.ts response shapes.
// ---------------------------------------------------------------------------

export type AnalyticsRange = '7d' | '30d' | '90d' | 'q' | 'y' | 'custom';

export interface RangeWindow {
  from: string;
  to: string;
  prevFrom: string;
  prevTo: string;
  days: number;
}

export interface AnalyticsOverview {
  range: RangeWindow;
  patients_total: number;
  patients_total_prev: number;
  patients_total_delta_pct: number | null;
  adherence_avg: number;
  adherence_avg_prev: number;
  adherence_avg_delta_pct: number | null;
  active_in_range: number;
  active_in_range_prev: number;
  active_in_range_delta_pct: number | null;
  conversion_rate: number;
  conversion_rate_prev: number;
  conversion_rate_delta_pct: number | null;
}

export interface RetentionCohort {
  cohort_month: string;
  size: number;
  retained_d7: number;
  retained_d30: number;
  retained_d90: number;
  retention_d7_pct: number;
  retention_d30_pct: number;
  retention_d90_pct: number;
}

export interface RetentionResponse {
  cohorts: RetentionCohort[];
}

export interface AdherenceBucket {
  range_label: string;
  min_pct: number;
  max_pct: number;
  count: number;
  patient_ids: string[];
}

export interface AdherenceDistribution {
  buckets: AdherenceBucket[];
  avg_adherence: number;
  total_patients: number;
  range: RangeWindow;
}

export interface AdherenceBucketDetail {
  patient_id: string;
  patient_name: string;
  human_id: string | null;
  adherence_pct: number;
  meal_checks_in_range: number;
  expected_in_range: number;
  last_check_at: string | null;
}

export interface AdherenceBucketDetailResponse {
  bucket: string;
  patients: AdherenceBucketDetail[];
  range: RangeWindow;
}

export interface WeightTargetReaching {
  patient_id: string;
  name: string;
  human_id: string | null;
  target_kg: number;
  start_kg: number | null;
  current_kg: number;
  delta_kg: number;
  target_delta_kg: number;
  progress_pct: number;
  days_active: number;
}

export interface WeightTargetResponse {
  total_with_goal: number;
  reached: number;
  reaching: WeightTargetReaching[];
}

export interface ConversionFunnel {
  invited: number;
  onboarded: number;
  first_plan: number;
  active_30d: number;
  conv_invited_onboarded_pct: number;
  conv_onboarded_first_plan_pct: number;
  conv_first_plan_active_pct: number;
  max_drop_off_step: 'invited' | 'onboarded' | 'first_plan' | 'active_30d' | null;
}

export type FunnelStep = 'invited' | 'onboarded' | 'first_plan' | 'active_30d';

export interface FunnelStepDetail {
  step: FunnelStep;
  patients_total: number;
  patients_stuck: number;
  stuck_list: Array<{
    patient_id: string;
    name: string;
    human_id: string | null;
    created_at: string;
    days_at_step: number;
  }>;
}

export interface ScheduledReport {
  id: string;
  pro_id: string;
  cadence: 'weekly' | 'monthly';
  format: 'html' | 'csv' | 'json';
  email: string;
  enabled: 0 | 1;
  last_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduledReportsResponse {
  reports: ScheduledReport[];
}

export interface SavedView {
  id: string;
  pro_id: string;
  name: string;
  config_json: string;
  is_default: 0 | 1;
  created_at: string;
  updated_at: string;
}

export interface SavedViewsResponse {
  views: SavedView[];
}

export interface CreateScheduledReportBody {
  cadence: 'weekly' | 'monthly';
  format?: 'html' | 'csv' | 'json';
  email?: string;
  enabled?: boolean;
}

export interface CreateSavedViewBody {
  name: string;
  config?: Record<string, unknown>;
  is_default?: boolean;
}

export interface UpsertWeightGoalBody {
  target_kg: number;
  start_kg?: number | null;
  target_date?: string | null;
  label?: string | null;
}

// Helpers

function analyticsQs(range: AnalyticsRange, from?: string | null, to?: string | null): string {
  const sp = new URLSearchParams();
  sp.set('range', range);
  if (range === 'custom') {
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
  }
  return `?${sp.toString()}`;
}

export function getAnalyticsOverview(
  range: AnalyticsRange = '30d', from?: string | null, to?: string | null,
): Promise<AnalyticsOverview> {
  return apiFetch<AnalyticsOverview>(`/api/pro/analytics/overview${analyticsQs(range, from, to)}`);
}

export function getAnalyticsRetention(): Promise<RetentionResponse> {
  return apiFetch<RetentionResponse>('/api/pro/analytics/retention');
}

export function getAnalyticsAdherence(
  range: AnalyticsRange = '30d', from?: string | null, to?: string | null,
): Promise<AdherenceDistribution> {
  return apiFetch<AdherenceDistribution>(`/api/pro/analytics/adherence${analyticsQs(range, from, to)}`);
}

export function getAnalyticsAdherenceBucket(
  label: string, range: AnalyticsRange = '30d', from?: string | null, to?: string | null,
): Promise<AdherenceBucketDetailResponse> {
  const sp = new URLSearchParams();
  sp.set('label', label);
  sp.set('range', range);
  if (range === 'custom') {
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
  }
  return apiFetch<AdherenceBucketDetailResponse>(`/api/pro/analytics/adherence/bucket?${sp.toString()}`);
}

export function getAnalyticsWeightTarget(): Promise<WeightTargetResponse> {
  return apiFetch<WeightTargetResponse>('/api/pro/analytics/weight-target');
}

export function getAnalyticsFunnel(): Promise<ConversionFunnel> {
  return apiFetch<ConversionFunnel>('/api/pro/analytics/funnel');
}

export function getAnalyticsFunnelStep(step: FunnelStep): Promise<FunnelStepDetail> {
  return apiFetch<FunnelStepDetail>(`/api/pro/analytics/funnel/step?step=${encodeURIComponent(step)}`);
}

export function exportAnalyticsUrl(format: 'csv' | 'json' | 'html' = 'csv'): string {
  return `${API_BASE}/api/pro/analytics/export?type=patients&format=${encodeURIComponent(format)}`;
}

export function listAnalyticsScheduledReports(): Promise<ScheduledReportsResponse> {
  return apiFetch<ScheduledReportsResponse>('/api/pro/analytics/scheduled');
}

export function createAnalyticsScheduledReport(body: CreateScheduledReportBody): Promise<{ report: ScheduledReport }> {
  return apiFetch<{ report: ScheduledReport }>('/api/pro/analytics/scheduled', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteAnalyticsScheduledReport(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/analytics/scheduled/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function listAnalyticsSavedViews(): Promise<SavedViewsResponse> {
  return apiFetch<SavedViewsResponse>('/api/pro/analytics/views');
}

export function createAnalyticsSavedView(body: CreateSavedViewBody): Promise<{ view: SavedView }> {
  return apiFetch<{ view: SavedView }>('/api/pro/analytics/views', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteAnalyticsSavedView(id: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/pro/analytics/views/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function upsertAnalyticsWeightGoal(patientId: string, body: UpsertWeightGoalBody): Promise<{ goal: unknown }> {
  return apiFetch<{ goal: unknown }>(`/api/pro/analytics/weight-target/${encodeURIComponent(patientId)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 10 — Genera dieta con AI (POST /api/pro/plan/generate)
// PUT /api/pro/patient/:id/anthropometrics
// ---------------------------------------------------------------------------

export type ActivityLevel =
  | 'sedentary'
  | 'lightly_active'
  | 'moderately_active'
  | 'very_active'
  | 'extra_active';

export type CookingSimplicityLevel = 'simple' | 'normal' | 'free';

export interface PatientAnthropometrics {
  height_cm: number | null;
  weight_kg: number | null;
  birth_date: string | null;   // 'YYYY-MM-DD'
  sex: 'M' | 'F' | null;
  activity_level: ActivityLevel | null;
  // Migration 0039 — abitudini di cucina + preferenze del paziente
  cooking_simplicity: CookingSimplicityLevel | null;
  food_preferences: string | null;
}

export interface UpdateAnthropometricsBody {
  height_cm: number;
  weight_kg: number;
  birth_date: string;   // 'YYYY-MM-DD'
  sex: 'M' | 'F';
  activity_level: ActivityLevel;
  /** Migration 0039 — opzionali: omessi = invariati lato server, null = azzerati. */
  cooking_simplicity?: CookingSimplicityLevel | null;
  food_preferences?: string | null;
}

export type MacroInput =
  | { mode: 'percentage'; protein_pct: number; fat_pct: number; carb_pct: number }
  | { mode: 'grams_per_kg'; protein_g_per_kg: number; fat_g_per_kg: number };

export type PatientGoalType = 'weight' | 'mass' | 'performance' | 'custom';

/** Deficit/surplus manuale: kcal/giorno o % del TDEE. Segno: <0 deficit, >0 surplus. */
export interface DeficitOverride {
  mode: 'kcal' | 'percent';
  value: number;
}

export interface GeneratePlanBody {
  patient_id: string;
  title: string;
  start_date: string;   // 'YYYY-MM-DD'
  weeks: number;        // 1-12
  macro_input: MacroInput;
  goal?: PatientGoalType;
  target_kcal_override?: number;
  deficit_override?: DeficitOverride;
  meal_distribution?: Partial<Record<MealType, number>>;
  active_meals?: MealType[];
}

export interface PlanMacros {
  protein_g: number;
  carb_g: number;
  fat_g: number;
}

export interface GeneratePlanResponse {
  plan_id: string;
  meals_created: number;
  items_created: number;
  warnings: string[];
  target_kcal: number;
  macros: PlanMacros;
}

export interface GeneratePlanError422 {
  error: 'missing_anthropometrics';
  field: keyof UpdateAnthropometricsBody;
}

// ─── Preview targets (no LLM, istantaneo) ─────────────────────────────────────

export interface PreviewTargetsBody {
  patient_id: string;
  macro_input: MacroInput;
  goal?: PatientGoalType;
  target_kcal_override?: number;
  deficit_override?: DeficitOverride;
  active_meals?: MealType[];
}

export interface PreviewPerMeal {
  meal_type: MealType;
  kcal: number;
  protein_g: number;
  carb_g: number;
  fat_g: number;
}

export interface PreviewTargetsResponse {
  bmr: number;
  tdee: number;
  goal: string;
  target_kcal: number;
  kcal_source: 'auto' | 'deficit' | 'override';
  delta_kcal: number;
  macros: PlanMacros;
  per_meal: PreviewPerMeal[];
}

export function previewPlanTargets(body: PreviewTargetsBody): Promise<PreviewTargetsResponse> {
  return apiFetch<PreviewTargetsResponse>('/api/pro/plan/preview-targets', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updatePatientAnthropometrics(
  patientId: string,
  body: UpdateAnthropometricsBody,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/pro/patient/${encodeURIComponent(patientId)}/anthropometrics`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

export function generateDietPlan(body: GeneratePlanBody): Promise<GeneratePlanResponse> {
  return apiFetch<GeneratePlanResponse>('/api/pro/plan/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 10 Fase 3/F5 — Subscription + consumo del piano (usage)
// Mirror di src/billing/me.ts (MySubscription + UsageInfo).
// ---------------------------------------------------------------------------

/** Consumo del pro vs limiti del piano. cap/quota null = illimitato. */
export interface UsageInfo {
  patients: { used: number; cap: number | null };
  /** F4: credits = crediti AI extra acquistati; totale disponibile = quota+credits. */
  ai: { used: number; quota: number | null; credits: number };
}

export interface MySubscription {
  subscription_status: string | null;
  plan_name: string | null;
  plan_price_cents: number | null;
  plan_currency: string | null;
  current_period_end: string | null;
  past_due_since: string | null;
  customer_portal_available: boolean;
  /** F4: add-on 2° modulo attivo. */
  module_addon_active: boolean;
  /** Presente SOLO per il pro; null per il paziente. */
  usage: UsageInfo | null;
}

export function getMySubscription(): Promise<MySubscription> {
  return apiFetch<MySubscription>('/api/billing/me');
}

/** F4 — avvia il checkout del pacchetto 100 azioni AI (one-time). */
export function buyAiPack(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('/api/billing/addon/ai-pack/checkout', { method: 'POST' });
}

/** F4 — avvia il checkout dell'add-on 2° modulo (subscription +9€/mese). */
export function buyModuleAddon(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('/api/billing/addon/module/checkout', { method: 'POST' });
}
